import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import AOS from 'aos';
import 'aos/dist/aos.css';
import axios from 'axios';

const Setem = () => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const [animatedItems, setAnimatedItems] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    AOS.init({ duration: 2000, once: false });
  }, []);

  useEffect(() => {
    getProducts();
  }, []);

  const getProducts = async () => {
    try {
      const response = await axios.get(
        `https://cortex-site.runasp.net/api/Setems`, 
        { withCredentials: false }
      );
      setServices(response.data);
      setSelectedService(response.data[0]);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const getLocalizedField = (item, fieldBase) => {
    if (!item) return '';
    if (currentLanguage === 'en') return item[`${fieldBase}En`] || item[fieldBase] || '';
    if (currentLanguage === 'ru') return item[`${fieldBase}Ru`] || item[fieldBase] || '';
    return item[fieldBase] || ''; // default: az
  };

  useEffect(() => {
    if (!services.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute('data-id');
            setAnimatedItems((prev) => [...new Set([...prev, id])]);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        root: null,
        rootMargin: '-100px 0px -50px 0px',
        threshold: 0.5,
      }
    );

    const items = document.querySelectorAll('.setem-animate');
    items.forEach((item) => observer.observe(item));

    return () => {
      items.forEach((item) => observer.unobserve(item));
    };
  }, [services]);

  return (
    <div data-aos="fade-up" className='mt-[56px] md:mt-[88px] bg-[#2a3d63]'>
      <div className='max-w-containerSm md:max-w-container mx-auto'>
        <p className='text-[24px] md:text-[32px] font-medium text-[white] pt-[45px]'>
          {t("SƏTƏM")}
        </p> 

        <div className='md:flex justify-between items-center mt-[32px] pb-[44px]'> 

          {/* Sol Kategori Listesi */}
          <div className='md:w-[30%] hidden md:flex flex-col gap-3'>
            {services.map((card, index) => {
              const isVisible = animatedItems.includes(card.id.toString());
              return (
                <div
                  key={card.id}
                  data-id={card.id}
                  onClick={() => setSelectedService(card)}
                  style={{ animationDelay: `${index * 0.15}s` }}
                 className={`setem-animate ${isVisible ? 'visible' : ''} w-full cursor-pointer px-4 py-3 rounded-lg font-medium bg-[white] ${
                    selectedService?.id === card?.id ? 'text-[#2a3d63]' : 'text-[#1C1C1C]'
                  }`}
                >
                 {getLocalizedField(card, "title")}
                </div>
              );
            })}
          </div>

          {/* Mobil Dropdown */}
          <div className="md:hidden z-30 relative w-full">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 text-[#1C1C1C] bg-white flex justify-between items-center"
            >
              {getLocalizedField(selectedService, "title")}
              <svg
                className={`w-4 h-4 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full left-0 w-full bg-white border border-gray-300 rounded-lg mt-2 z-40 px-2 py-2">
                <div className="flex flex-col space-y-2">
                  {services.map((service) => (
                    <div
                      key={service.id}
                      onClick={() => {
                        setSelectedService(service);
                        setDropdownOpen(false);
                      }}
                      className={`px-4 py-2 cursor-pointer hover:bg-gray-100 rounded-md ${
                        selectedService?.id === service?.id ? 'bg-[#2a3d63] text-white font-medium' : ''
                      }`}
                    >
                      {getLocalizedField(service, "title")}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sağ İçerik */}
          <AnimatePresence mode="wait">
            {selectedService && (
              <div
                className='relative md:flex flex-col md:flex-row items-center mt-8 md:mt-0 md:w-[60%]'
                key={selectedService?.id}
              >
                {/* Görsel */}
                <motion.div
                  key={selectedService?.id + '-img'}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.4 }}
                  className='md:w-[52%] ml-auto z-0'
                >
                  <img className='w-full h-full object-cover' src={`https://cortex-site.runasp.net/${selectedService?.image}`} />
                </motion.div>

                {/* Metin Kutusu */}
                <motion.div
                  key={selectedService?.id + '-text'}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -30 }}
                  transition={{ duration: 0.4 }}
                  className='relative z-10 w-full md:w-[52%] h-auto md:min-h-[216px] bg-white py-5 px-6 border border-[#E5E5E5] rounded-[4px] 
                    -mt-10 md:mt-0 md:absolute md:left-0'
                >
                  <p className='text-[#1C1C1C] font-medium text-[18px] md:text-[24px]'>
                    {getLocalizedField(selectedService, "title")}
                  </p>
                  <div
                    className="mt-6"
                   
                  > {getLocalizedField(selectedService, "description").replace(/<[^>]*>/g, '')}</div>
                </motion.div>
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Setem;
